import type { ArrayPredicate } from 'Core/types';
import facetsConfig from 'Models/uiConfig/facetsConfig';
import { DisplayMode, ServerModel } from 'Modules/serverApi/types';
import isTruthy from 'Utils/isTruthy';
import { removeSpecialSymbols } from 'Utils/string';

import FacetValue, { type FacetValueFull } from './facetValue';

export default class Facet {
  alwaysShowCount: number;
  field: string;
  displayMode: DisplayMode;
  name: string;
  totalHits: number;
  ranges: string[] | null;
  selection: FacetValueFull[];
  treeSeparator: string | null;
  values: FacetValueFull[];

  _facetedValuesPredicates: ArrayPredicate<FacetValueFull>[] = [];
  _facet: ServerModel.FacetCategory;

  constructor(
    facet: ServerModel.FacetCategory,
    valuesFilter?: ArrayPredicate<FacetValueFull>,
    baseTreeLevel?: number,
  ) {
    this.alwaysShowCount = facet.AlwaysShowCount ?? 0;
    this.field = facet.FieldName;
    this.displayMode = facet.DisplayMode ?? DisplayMode.Default;
    this.name = facet.DisplayName || this.field;
    this.ranges = facet.Ranges ?? [];
    this.totalHits = facet.TotalHits ?? 0;
    this.selection = FacetValue.createManyFromSearchResponse(facet.Selection ?? [], facet, baseTreeLevel);
    this.treeSeparator = facet.TreeSeparator ?? null;
    this.values = FacetValue.createManyFromSearchResponse(facet.Values ?? [], facet, baseTreeLevel);

    setFacetedValuesProcessors(this, valuesFilter);

    // temporary legacy compatibility fix
    this._facet = { ...facet, Values: facet.Values ?? [] };
  }

  get facetedSelection(): FacetValueFull[] {
    return getFaceted(this, this.selection);
  }

  get facetedValues(): FacetValueFull[] {
    return getFaceted(this, this.values);
  }

  getFilteredValues(valueToFilter: string, includeSelected: boolean = true): FacetValueFull[] {
    return !valueToFilter
      ? this.facetedValues
      : this.facetedValues.filter((v) => {
          const lowerFacetValue = v.value.toLowerCase();
          const lowerFilterValue = valueToFilter.toLowerCase();

          return (
            (includeSelected && v.isSelected) ||
            lowerFacetValue.includes(lowerFilterValue) ||
            removeSpecialSymbols(lowerFacetValue).includes(removeSpecialSymbols(lowerFilterValue))
          );
        });
  }
}

function getFaceted(self: Facet, values: FacetValueFull[]) {
  return values.filter((...args) => self._facetedValuesPredicates.every((predicate) => predicate(...args)));
}

function setFacetedValuesProcessors(self: Facet, customPredicate?: ArrayPredicate<FacetValueFull>) {
  const hideSelectedTreeValues =
    facetsConfig.hideSelectedTreeValues && self.displayMode === DisplayMode.Tree
      ? (v: FacetValueFull) => !v.isSelected
      : undefined;
  const hideNonUniqueRangedValues =
    self.displayMode === DisplayMode.Slider || self.displayMode === DisplayMode.Ranges
      ? (v: FacetValueFull) => v.isUnique || !!v.hitCount
      : undefined;
  const predicates: Array<ArrayPredicate<FacetValueFull> | false | undefined> = [
    hideSelectedTreeValues,
    hideNonUniqueRangedValues,
    customPredicate,
    (v: FacetValueFull) => Number(v.treeLevel) >= 0,
  ];

  self._facetedValuesPredicates = predicates.filter(isTruthy);
}
